/**
 * /pm-board — the project manager's day (Sep 11, 2026).
 *
 * Greg: "a project manager board. mobile friendly. maybe a 7 day calendar at
 * top, they can click on a job and it provides easily clickable options for
 * their duties while on site … a guide through day that they can check off …
 * send a return pick up email to srs at end of day … allow jennifer, shannon to
 * edit list of responsibilities and tasks … build out a to do / not do."
 *
 * Layout (phone first):
 *   ┌ 7-day strip (‹ › shift a week, Today) — build count per day
 *   ├ job cards for the selected day (+ "still open" carry-overs on today)
 *   └ tap a job → PmJobSheet (full-screen on phones): Do/Don't, the guide with
 *     checkboxes + camera buttons, the SRS return-pickup section.
 *   Setup tab (setup tier only) → PmSetup: edit the guide + the Do/Don't list.
 *
 * Data comes ONLY through lib/pmBoard.ts — never a direct deals read here
 * (Ramiro's role has no deals policy; the RPC is the narrow read path).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useRealtimeTable } from "../lib/useRealtimeTable";
import { useCompanyCamRealtime } from "../lib/companycam";
import { useTabParam } from "../lib/useTabParam";
import {
  addDaysISO, fetchJobState, fetchPmJobs, fetchRules, fetchTemplates, fmtDay, jobProgress, todayISO,
  type PmJob, type PmJobPhoto, type PmJobTask, type PmReturnSend, type PmRule, type PmTemplate,
} from "../lib/pmBoard";
import PmJobSheet from "../components/pm/PmJobSheet";
import PmSetup from "../components/pm/PmSetup";
import RulesList from "../components/pm/RulesList";

const SETUP_ROLES = new Set(["admin", "sales_director", "director_of_operations", "finance"]);
type Tab = "board" | "setup";
const TABS: Tab[] = ["board", "setup"];

export interface JobState {
  tasks: Record<string, PmJobTask[]>;
  photos: Record<string, PmJobPhoto[]>;
  /** Tag lists of each job's tagged CompanyCam photos (fetchJobState). */
  ccTags: Record<string, string[][]>;
  returns: Record<string, PmReturnSend>;
}

export default function PmBoard() {
  const { member, role, isSuperUser } = useAuth();
  const canEditSetup = !!isSuperUser || (!!role && SETUP_ROLES.has(role));
  const [tab, setTab] = useTabParam<Tab>("tab", "board", TABS);
  const [params, setParams] = useSearchParams();

  const today = todayISO();
  const dayParam = params.get("day");
  const selectedDay = dayParam && /^\d{4}-\d{2}-\d{2}$/.test(dayParam) ? dayParam : today;
  const [weekStart, setWeekStart] = useState<string>(selectedDay);
  const selectedDeal = params.get("deal");

  const setDay = useCallback((iso: string) => {
    const p = new URLSearchParams(window.location.search);
    if (iso === todayISO()) p.delete("day"); else p.set("day", iso);
    setParams(p, { replace: true });
  }, [setParams]);
  const openDeal = useCallback((id: string | null) => {
    const p = new URLSearchParams(window.location.search);
    if (id) p.set("deal", id); else p.delete("deal");
    setParams(p, { replace: id === null });
  }, [setParams]);

  // ── data ──
  const [jobs, setJobs] = useState<PmJob[] | null>(null);
  const [templates, setTemplates] = useState<PmTemplate[]>([]);
  const [rules, setRules] = useState<PmRule[]>([]);
  const [state, setState] = useState<JobState>({ tasks: {}, photos: {}, ccTags: {}, returns: {} });
  const [names, setNames] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  const weekEnd = addDaysISO(weekStart, 6);

  const loadJobs = useCallback(async () => {
    try {
      // Window = the visible week, plus the carry-over lookback the RPC adds.
      const list = await fetchPmJobs(weekStart, weekEnd);
      setJobs(list);
      setErr(null);
      const st = await fetchJobState(list.map((j) => j.id));
      setState(st);
    } catch (e) {
      setErr((e as Error).message);
      setJobs((prev) => prev ?? []);
    }
  }, [weekStart, weekEnd]);

  const loadGuide = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([fetchTemplates(false), fetchRules(false)]);
      setTemplates(t); setRules(r);
    } catch (e) { setErr((e as Error).message); }
  }, []);

  useEffect(() => { void loadJobs(); }, [loadJobs]);
  useEffect(() => { void loadGuide(); }, [loadGuide]);
  useEffect(() => {
    supabase.from("team_roster").select("id,full_name").then(({ data }) => {
      const m: Record<string, string> = {};
      for (const r of (data ?? []) as Array<{ id: string; full_name: string }>) m[r.id] = r.full_name;
      setNames(m);
    });
  }, []);

  // Realtime — state tables + guide tables + deals (dates move on the dispatch board).
  useRealtimeTable("pm_job_tasks", loadJobs);
  useRealtimeTable("pm_job_photos", loadJobs);
  useRealtimeTable("pm_return_pickup_emails", loadJobs);
  useCompanyCamRealtime((jobs ?? []).map((j) => j.id), loadJobs);
  useRealtimeTable("deals", loadJobs, "UPDATE");
  useRealtimeTable("pm_task_templates", loadGuide);
  useRealtimeTable("pm_rules", loadGuide);

  // If the selected day leaves the visible week (deep link), snap the strip.
  useEffect(() => {
    if (selectedDay < weekStart || selectedDay > weekEnd) setWeekStart(selectedDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDaysISO(weekStart, i)), [weekStart]);
  const countByDay = useMemo(() => {
    const m: Record<string, number> = {};
    for (const j of jobs ?? []) if (j.build_scheduled_date) m[j.build_scheduled_date] = (m[j.build_scheduled_date] ?? 0) + 1;
    return m;
  }, [jobs]);

  const dayJobs = useMemo(() => (jobs ?? []).filter((j) => j.build_scheduled_date === selectedDay), [jobs, selectedDay]);
  const carryOver = useMemo(() => {
    if (selectedDay !== today) return [];
    return (jobs ?? []).filter((j) => !!j.build_scheduled_date && j.build_scheduled_date < today && !j.build_completed_date);
  }, [jobs, selectedDay, today]);

  const openJob = selectedDeal ? (jobs ?? []).find((j) => j.id === selectedDeal) ?? null : null;

  /* ── Setup tab ─────────────────────────────────────────────────────── */
  if (tab === "setup" && canEditSetup) {
    return (
      <div className="mx-auto max-w-3xl px-3 py-4 sm:px-6">
        <Header tab={tab} setTab={setTab} canEditSetup={canEditSetup} />
        <PmSetup memberId={member?.id ?? null} />
      </div>
    );
  }

  /* ── Job sheet (full screen on phones) ─────────────────────────────── */
  if (selectedDeal) {
    return (
      <div className="mx-auto max-w-3xl px-3 py-4 sm:px-6">
        {openJob ? (
          <PmJobSheet
            job={openJob}
            templates={templates}
            rules={rules}
            tasks={state.tasks[openJob.id] ?? []}
            photos={state.photos[openJob.id] ?? []}
            lastReturn={state.returns[openJob.id] ?? null}
            names={names}
            memberId={member?.id ?? null}
            onBack={() => openDeal(null)}
            onChanged={loadJobs}
          />
        ) : jobs === null ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading job…</div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
            That job isn't in the current window. <button className="font-semibold text-orange-600" onClick={() => openDeal(null)}>Back to the board</button>
          </div>
        )}
      </div>
    );
  }

  /* ── Board ─────────────────────────────────────────────────────────── */
  return (
    <div className="mx-auto max-w-3xl px-3 py-4 sm:px-6">
      <Header tab={tab} setTab={setTab} canEditSetup={canEditSetup} />

      {err && (
        <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Couldn't load the board: {err}
        </div>
      )}

      {/* 7-day strip */}
      <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        <div className="mb-1 flex items-center justify-between px-1">
          <button className="rounded-lg px-3 py-1.5 text-lg leading-none text-slate-600 active:bg-slate-100" aria-label="Previous week"
            onClick={() => setWeekStart(addDaysISO(weekStart, -7))}>‹</button>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <span>{fmtDay(weekStart, { month: "short", day: "numeric" })} – {fmtDay(weekEnd, { month: "short", day: "numeric" })}</span>
            {(weekStart !== today || selectedDay !== today) && (
              <button className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-bold text-orange-700"
                onClick={() => { setWeekStart(today); setDay(today); }}>Today</button>
            )}
          </div>
          <button className="rounded-lg px-3 py-1.5 text-lg leading-none text-slate-600 active:bg-slate-100" aria-label="Next week"
            onClick={() => setWeekStart(addDaysISO(weekStart, 7))}>›</button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => {
            const sel = d === selectedDay;
            const isToday = d === today;
            const n = countByDay[d] ?? 0;
            return (
              <button key={d} onClick={() => setDay(d)}
                className={`flex min-h-[64px] flex-col items-center justify-center rounded-xl border px-1 py-2 transition
                  ${sel ? "border-orange-500 bg-orange-500 text-white shadow" : isToday ? "border-orange-300 bg-orange-50 text-slate-800" : "border-slate-200 bg-white text-slate-700 active:bg-slate-50"}`}>
                <span className={`text-[10px] font-semibold uppercase ${sel ? "text-orange-100" : "text-slate-400"}`}>{fmtDay(d, { weekday: "short" })}</span>
                <span className="text-lg font-extrabold leading-tight">{fmtDay(d, { day: "numeric" })}</span>
                <span className={`mt-0.5 h-4 text-[11px] font-bold ${n ? (sel ? "text-white" : "text-orange-600") : "text-transparent"}`}>
                  {n ? `${n} job${n === 1 ? "" : "s"}` : "·"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Day heading */}
      <div className="mt-4 flex items-baseline justify-between px-1">
        <h2 className="text-base font-bold text-slate-900">
          {selectedDay === today ? "Today" : fmtDay(selectedDay, { weekday: "long" })}
          <span className="ml-2 text-sm font-normal text-slate-500">{fmtDay(selectedDay, { month: "long", day: "numeric" })}</span>
        </h2>
        <span className="text-xs text-slate-500">{dayJobs.length} build{dayJobs.length === 1 ? "" : "s"}</span>
      </div>

      {jobs === null ? (
        <div className="mt-3 space-y-2">{[0, 1].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : dayJobs.length === 0 ? (
        <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          No builds on the calendar for this day.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {dayJobs.map((j) => (
            <JobCard key={j.id} job={j} templates={templates} state={state} names={names} onOpen={() => openDeal(j.id)} />
          ))}
        </div>
      )}

      {carryOver.length > 0 && (
        <>
          <div className="mt-6 flex items-baseline justify-between px-1">
            <h3 className="text-sm font-bold text-amber-800">Still open from earlier</h3>
            <span className="text-xs text-slate-500">not marked complete</span>
          </div>
          <div className="mt-2 space-y-2">
            {carryOver.map((j) => (
              <JobCard key={j.id} job={j} templates={templates} state={state} names={names} onOpen={() => openDeal(j.id)} carry />
            ))}
          </div>
        </>
      )}

      {rules.length > 0 && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Every day</div>
          <RulesList rules={rules} />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Header({ tab, setTab, canEditSetup }: { tab: Tab; setTab: (t: Tab) => void; canEditSetup: boolean }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <div>
        <h1 className="text-xl font-extrabold text-slate-900">🧰 PM Board</h1>
        <p className="text-xs text-slate-500">Your builds, the photo guide, and the end-of-day SRS return.</p>
      </div>
      {canEditSetup && (
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs font-semibold">
          {(["board", "setup"] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`rounded-md px-3 py-1.5 ${tab === t ? "bg-slate-900 text-white" : "text-slate-600"}`}>
              {t === "board" ? "Board" : "⚙ Setup"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function JobCard({ job, templates, state, names, onOpen, carry }: {
  job: PmJob; templates: PmTemplate[]; state: JobState; names: Record<string, string>; onOpen: () => void; carry?: boolean;
}) {
  const ret = state.returns[job.id];
  const returnSent = !!ret && ret.send_status === "sent";
  const prog = jobProgress(templates, state.tasks[job.id], state.photos[job.id], returnSent, state.ccTags[job.id]);
  const pct = prog.total ? Math.round((prog.done / prog.total) * 100) : 0;
  const complete = !!job.build_completed_date;
  const rm = job.owner_id ? names[job.owner_id] : null;
  const wb = (job.pm_workbook ?? {}) as Record<string, unknown>;
  const ev = (wb.eagleview ?? {}) as Record<string, unknown>;
  const sq = Number(ev.sq_with_waste) || Number(job.total_squares) || null;

  return (
    <button onClick={onOpen}
      className={`w-full rounded-2xl border bg-white p-3 text-left shadow-sm transition active:bg-slate-50 ${carry ? "border-amber-300" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-base font-bold text-slate-900">{job.homeowner_name || "(no name)"}</div>
          <div className="truncate text-sm text-slate-600">{job.property_street}{job.property_city ? `, ${job.property_city}` : ""}</div>
          <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
            {rm && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">RM {rm.split(" ")[0]}</span>}
            {sq && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{sq} sq</span>}
            {job.shingle_color && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{job.shingle_color}</span>}
            {carry && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">{fmtDay(job.build_scheduled_date!)}</span>}
            {complete && <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">Built ✓</span>}
            {returnSent && <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">SRS return sent</span>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`text-lg font-extrabold ${pct === 100 ? "text-emerald-600" : "text-slate-900"}`}>{prog.done}<span className="text-sm font-semibold text-slate-400">/{prog.total}</span></div>
          <div className="text-[10px] font-semibold uppercase text-slate-400">done</div>
        </div>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${pct === 100 ? "bg-emerald-500" : "bg-orange-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </button>
  );
}
